// Feature detail page content + shared brand config
import {
  Boxes, MapPin, ScanLine, ClipboardCheck, ArrowLeftRight, History,
  Container, PackageSearch, FileText, ShoppingCart, AlertTriangle, Layers, Warehouse, ListTree,
  Truck, Waypoints, PackageCheck, ShieldCheck, Route, Undo2,
  CalendarClock, TrendingUp, Target, Boxes as BoxesIcon, Users, Ruler,
  Building2, Split, Receipt, LayoutDashboard, Gauge,
  Factory, ClipboardList, Combine, Beaker, Percent,
  Plug, Webhook, Network, Store, CircleDollarSign,
} from "lucide-react";

export const FEATURE_ORDER = [
  "inventory", "inbound", "outbound", "master-planner",
  "pallet-control", "production", "3pl", "integrations",
];

export const FEATURE_PAGES = {
  inventory: {
    slug: "inventory",
    label: "Inventory",
    eyebrow: "Feature · Inventory",
    title: "Know exactly what you have.",
    sub: "Real-time inventory visibility across your warehouse, locations and customers — without waiting for an end-of-day report.",
    stats: [
      { k: "Stock accuracy", v: "99.6%" },
      { k: "Locations tracked", v: "Unlimited" },
      { k: "Count method", v: "Perpetual" },
    ],
    features: [
      { icon: Boxes, title: "Real-time inventory", desc: "Know your stock position instantly — never wait for an end-of-day report again." },
      { icon: MapPin, title: "Location control", desc: "Know exactly where stock is stored, down to the bin." },
      { icon: ScanLine, title: "Barcode scanning", desc: "Reduce manual entry and eliminate stock errors at the source." },
      { icon: ClipboardCheck, title: "Cycle counting", desc: "Continuously verify inventory accuracy without stopping operations." },
      { icon: ArrowLeftRight, title: "Transfers", desc: "Move stock between locations with a complete audit trail." },
      { icon: History, title: "Inventory history", desc: "See who moved what, where and when — every movement logged." },
    ],
  },
  inbound: {
    slug: "inbound",
    label: "Inbound",
    eyebrow: "Feature · Inbound",
    title: "Know what's coming before it arrives.",
    sub: "Manage your entire inbound operation from purchase order through receiving and putaway.",
    flow: ["Purchase Order", "Container", "Receiving", "Palletisation", "Putaway", "Available Stock"],
    features: [
      { icon: Container, title: "Container receiving", desc: "Track containers from booking to unload with full visibility." },
      { icon: PackageSearch, title: "Advanced receiving", desc: "Blind, expected and over-receipt handling built in." },
      { icon: FileText, title: "ASN", desc: "Ingest advance shipping notices to speed up the dock." },
      { icon: ShoppingCart, title: "Purchase orders", desc: "Receive directly against POs and reconcile automatically." },
      { icon: AlertTriangle, title: "Receiving discrepancies", desc: "Flag shortages, damages and overs the moment they happen." },
      { icon: Layers, title: "Pallet receiving", desc: "Capture pallet types and quantities as stock lands." },
      { icon: Warehouse, title: "Putaway", desc: "Directed putaway that guides stock to the right home." },
      { icon: MapPin, title: "Location allocation", desc: "Smart slotting suggestions based on velocity and space." },
      { icon: ListTree, title: "Receiving history", desc: "A complete, searchable record of everything received." },
    ],
  },
  outbound: {
    slug: "outbound",
    label: "Outbound",
    eyebrow: "Feature · Outbound",
    title: "Pick accurately. Pack efficiently. Dispatch with confidence.",
    sub: "Guide every operator through a validated outbound workflow — from order to dispatch — with zero guesswork.",
    flow: ["Order", "Allocate", "Pick", "Scan", "Pack", "Verify", "Dispatch"],
    features: [
      { icon: Waypoints, title: "Order allocation", desc: "Automatically allocate stock to orders by rule and priority." },
      { icon: Route, title: "Wave & batch picking", desc: "Group orders into efficient picking waves and routes." },
      { icon: ScanLine, title: "RF scanning", desc: "Scan-to-confirm every pick to guarantee accuracy." },
      { icon: PackageCheck, title: "Pack verification", desc: "Verify contents at pack to stop the wrong box shipping." },
      { icon: Truck, title: "Carrier integration", desc: "Generate labels and manifests with your carriers." },
      { icon: ShieldCheck, title: "Dispatch control", desc: "Final validation gate before anything leaves the door." },
      { icon: Undo2, title: "Returns", desc: "Process returns back into stock with full traceability." },
      { icon: History, title: "Pick history", desc: "Track productivity and accuracy per operator over time." },
    ],
  },
  "master-planner": {
    slug: "master-planner",
    label: "Master Planner",
    eyebrow: "Feature · Master Planner",
    title: "Plan tomorrow before it becomes a problem.",
    sub: "Model any scenario — a new customer, a peak season, a wave of containers — and see the impact on capacity before it hits the floor.",
    stats: [
      { k: "Scenarios", v: "Unlimited" },
      { k: "Forecast horizon", v: "12 months" },
      { k: "Capacity alerts", v: "Real-time" },
    ],
    features: [
      { icon: Target, title: "Scenario modelling", desc: "Ask 'what if?' and see the capacity impact instantly." },
      { icon: Gauge, title: "Capacity forecasting", desc: "Project utilisation weeks and months ahead." },
      { icon: TrendingUp, title: "Demand planning", desc: "Blend seasonality and growth into a clear picture." },
      { icon: Container, title: "Container scheduling", desc: "Sequence inbound so the dock never floods." },
      { icon: Users, title: "Labour planning", desc: "Right-size your team against forecast workload." },
      { icon: Ruler, title: "Space optimisation", desc: "Find the space you didn't know you had." },
    ],
  },
  "pallet-control": {
    slug: "pallet-control",
    label: "Pallet Control",
    eyebrow: "Feature · Pallet Control",
    title: "Stop losing pallets.",
    sub: "Track every movement across CHEP, LOSCAM and more — then reconcile against provider statements automatically and drill straight into any variance.",
    flow: ["Receive", "Store", "Pick", "Dispatch", "Customer", "Return"],
    stats: [
      { k: "Providers", v: "CHEP · LOSCAM · +" },
      { k: "Reconciliation", v: "Automatic" },
      { k: "Audit trail", v: "Complete" },
    ],
    features: [
      { icon: ArrowLeftRight, title: "Movement tracking", desc: "Every pallet in and out, captured as it happens." },
      { icon: ClipboardCheck, title: "Account reconciliation", desc: "Match your position to the provider statement automatically." },
      { icon: AlertTriangle, title: "Variance detection", desc: "Surface discrepancies the moment they appear." },
      { icon: FileText, title: "Provider statements", desc: "Import CHEP / LOSCAM statements and compare." },
      { icon: Receipt, title: "Transfer dockets", desc: "Digital dockets for every pallet transfer." },
      { icon: History, title: "Balance reporting", desc: "Know your true pallet balance at any moment." },
    ],
  },
  production: {
    slug: "production",
    label: "Production",
    eyebrow: "Feature · Production",
    title: "From raw materials to finished goods.",
    sub: "Manage light manufacturing and kitting inside your warehouse — consume components, build products and track every batch.",
    flow: ["Raw Materials", "Work Order", "Assembly", "QA", "Finished Goods", "Stock"],
    features: [
      { icon: ClipboardList, title: "Bill of materials", desc: "Define recipes and component requirements per product." },
      { icon: Factory, title: "Work orders", desc: "Raise and track production runs end to end." },
      { icon: Combine, title: "Component consumption", desc: "Draw down raw materials automatically as you build." },
      { icon: BoxesIcon, title: "Batch tracking", desc: "Full lot and batch traceability for compliance." },
      { icon: PackageCheck, title: "Kitting & assembly", desc: "Assemble kits and bundles with guided steps." },
      { icon: Percent, title: "Yield reporting", desc: "Measure output, waste and efficiency per run." },
    ],
  },
  "3pl": {
    slug: "3pl",
    label: "3PL",
    eyebrow: "Feature · 3PL",
    title: "One platform. Multiple customers.",
    sub: "Run every client from a single system with complete data separation, per-customer billing and dedicated portals.",
    stats: [
      { k: "Customers", v: "Unlimited" },
      { k: "Data separation", v: "Complete" },
      { k: "Billing", v: "Per-customer" },
    ],
    features: [
      { icon: Building2, title: "Multi-customer", desc: "Onboard clients without spinning up new instances." },
      { icon: Split, title: "Data separation", desc: "Each customer sees only their own stock and orders." },
      { icon: CircleDollarSign, title: "Activity-based billing", desc: "Bill for storage, handling and movements accurately." },
      { icon: LayoutDashboard, title: "Customer portals", desc: "Give clients live visibility of their inventory." },
      { icon: Receipt, title: "Per-customer invoicing", desc: "Generate clean, itemised invoices per client." },
      { icon: ShieldCheck, title: "Custom SLAs", desc: "Track service levels for every customer agreement." },
    ],
  },
  integrations: {
    slug: "integrations",
    label: "Integrations",
    eyebrow: "Feature · Integrations",
    title: "Connect your entire stack.",
    sub: "Plug Stockaris into your ERP, e-commerce, carriers and accounting tools with a modern API and pre-built connectors.",
    stats: [
      { k: "API", v: "REST + Webhooks" },
      { k: "Connectors", v: "Pre-built" },
      { k: "Sync", v: "Real-time" },
    ],
    features: [
      { icon: Plug, title: "REST API", desc: "A clean, documented API for anything custom." },
      { icon: Webhook, title: "Webhooks", desc: "Push real-time events into your other systems." },
      { icon: Network, title: "ERP connectors", desc: "Sync stock, orders and masters with your ERP." },
      { icon: Store, title: "E-commerce sync", desc: "Keep Shopify and marketplaces in lockstep." },
      { icon: Truck, title: "Carrier integrations", desc: "Rates, labels and tracking with major carriers." },
      { icon: FileText, title: "Accounting sync", desc: "Flow invoices and costs into your finance stack." },
    ],
  },
};
